/**
 * Shared ticket category + subcategory taxonomy.
 *
 * Aligned to the company's actual field-service work types (CEO-approved
 * 2026-05-29) instead of the generic IT-ops list, so reporting groups by
 * the work we actually dispatch: Break/Fix, IMAC, builds, cabling,
 * racking, Wi-Fi, rollouts, remote support.
 *
 * Shared by Create.tsx + EditTicketModal.tsx so both surfaces offer the
 * same options (reports stratify on consistent data). Two-level taxonomy
 * (category → subcategory) matches the ServiceNow / Freshservice pattern.
 * Backend stores both as free text (varchar 100), so changing this list
 * never needs a migration, and existing tickets keep their stored values.
 */
export const TICKET_CATEGORIES = [
    'Break/Fix (EUC)',
    'IMAC',
    'Desktop Setup',
    'Laptop Setup',
    'Cabling',
    'Racking & Stacking',
    'Wi-Fi Survey',
    'AP Installation',
    'AP Re-location',
    'Hardware Rollout',
    'Remote Support',
    'Other',
];
export const TICKET_SUBCATEGORIES = {
    'Break/Fix (EUC)': [
        'Laptop', 'Desktop', 'Monitor / Display', 'Printer / Scanner',
        'Docking Station', 'Peripheral (keyboard / mouse)', 'Mobile Device', 'Other',
    ],
    IMAC: ['Install', 'Move', 'Add', 'Change', 'Decommission', 'Other'],
    'Desktop Setup': [
        'New Build', 'Re-image', 'Domain Join', 'Software Load', 'Data Migration', 'Other',
    ],
    'Laptop Setup': [
        'New Build', 'Re-image', 'Encryption', 'Software Load', 'Data Migration', 'Other',
    ],
    Cabling: [
        'Cat6 / Cat6a Run', 'Patch Panel', 'Fibre', 'Labelling',
        'Testing / Certification', 'Other',
    ],
    'Racking & Stacking': [
        'Rack Install', 'Device Mount', 'Power / PDU', 'Cable Management', 'Decommission', 'Other',
    ],
    'Wi-Fi Survey': [
        'Predictive', 'Pre-deployment', 'Post-deployment / Validation', 'Heatmap', 'Other',
    ],
    // Owner-requested 2026-06-24 — Wi-Fi access-point work. Kept
    // separate from Wi-Fi Survey (survey = planning, install/relocate =
    // execution) and from each other (Install adds a NEW device,
    // Re-location MOVES an existing one — different parts, different
    // checklist, different time budget).
    'AP Installation': [
        'Ceiling-mount', 'Wall-mount', 'Pole-mount', 'PoE Switch Port Config',
        'Antenna Adjustment', 'Other',
    ],
    'AP Re-location': [
        'Re-position', 'Re-cable', 'Reconfigure Channel / Power', 'Antenna Re-aim',
        'Replace Mount', 'Other',
    ],
    'Hardware Rollout': [
        'Deployment', 'Refresh', 'Asset Tagging', 'Disposal / WEEE', 'Other',
    ],
    'Remote Support': [
        'Troubleshooting', 'Software', 'Account / Access', 'Configuration', 'Other',
    ],
};
/** Lookup that returns the available subcategories for a given category,
 *  or an empty array when none apply ('' or 'Other'). */
export function subcategoriesFor(cat) {
    if (cat === '' || cat === 'Other')
        return [];
    // Defensive: a ticket whose stored category isn't in the current taxonomy
    // (legacy / imported / seed data) must not crash the edit form — an unknown
    // category simply has no known subcategories.
    return TICKET_SUBCATEGORIES[cat] ?? [];
}
// ---- Dynamic tenant taxonomy (2026-08-06) -----------------------------------
// Fetches the tenant's configurable list; falls back to the static defaults
// while loading / on error so the create form is never empty.
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
export function useTicketCategories() {
    const q = useQuery({
        queryKey: ['ticket-categories'],
        queryFn: () => api.get('/ticket-categories'),
        staleTime: 5 * 60_000,
    });
    if (q.data && q.data.length > 0) {
        const map = Object.fromEntries(q.data.map((c) => [c.name, c.subcategories.map((x) => x.name)]));
        return {
            categories: q.data.map((c) => c.name),
            subcategoriesFor: (cat) => map[cat] ?? [],
        };
    }
    return { categories: TICKET_CATEGORIES, subcategoriesFor };
}
